/**
 * The post-game ranking history (SPEC §2.6): every completed ranking of the
 * game — the question, who was ranking, and the full first-to-last order.
 *
 * ## What this is for
 *
 * This is the argument the room is already having. Superlatives assert
 * conclusions ("Sam was picked for bottom 7 times") without showing the
 * working, and by the last round nobody remembers turn 2.
 *
 * ## What is deliberately absent, and must stay absent
 *
 * No share. No export. No download. No save-as-image. No `<canvas>`, and
 * nothing the browser can be talked into rendering to a file. That is a privacy
 * decision (SPEC §6), re-confirmed explicitly for this surface: a rendered
 * image of the history is twelve identifiable faces plus names in a file, which
 * is precisely the artefact this app is built never to produce. The room can
 * look at a phone.
 *
 * ## Shape, at 390×844
 *
 * A ten-round twelve-player game is 120 turns, each with a twelve-row ranking —
 * ~1,500 rows, which as one list is not a review surface, it is a wall. So:
 *
 *  - a **round filter** cuts the list to one pass (at most twelve turns), and
 *  - each turn is a **collapsed row** — number, question, who ranked — that
 *    opens to the full order, one at a time.
 *
 * Nothing here animates. It is reference material for an argument happening out
 * loud in the room, and the game's live moments (the reveal) are the ones that
 * earn motion.
 */
import { useMemo, useState } from 'react';
import type { PlayerId, RankingHistoryEntry } from '@idgame/protocol';
import { LicenseCard, type LicensePlayer } from '../LicenseCard.js';
import { Eyebrow } from '../ui.js';

export interface RankingHistoryListProps {
  /** Oldest turn first, exactly as the server sent it. */
  history: readonly RankingHistoryEntry[];
  players: ReadonlyMap<PlayerId, LicensePlayer>;
  selfId?: PlayerId;
}

/** A player who has been removed from the roster still ranked, and still ranks. */
function playerOf(
  players: ReadonlyMap<PlayerId, LicensePlayer>,
  id: PlayerId,
  selfId?: PlayerId,
): LicensePlayer {
  const known = players.get(id);
  if (known) return { ...known, isSelf: id === selfId };
  return { id, name: 'Unknown', photo: null };
}

/**
 * "All" is the honest default, but at ten rounds it is a 12,000px scroll on a
 * 390px phone — which is the thing this component exists to avoid. Past two
 * rounds we open on the first round instead; the filter is right there.
 */
function defaultRound(rounds: readonly number[]): number | null {
  return rounds.length > 2 ? (rounds[0] ?? null) : null;
}

export function RankingHistoryList({ history, players, selfId }: RankingHistoryListProps) {
  const rounds = useMemo(() => {
    const seen = new Set<number>();
    for (const entry of history) seen.add(entry.round);
    return [...seen].sort((a, b) => a - b);
  }, [history]);

  const [picked, setPicked] = useState<number | null>(() => defaultRound(rounds));
  const [openTurn, setOpenTurn] = useState<number | null>(null);

  /**
   * The history prop is replaced wholesale — a reconnect in `superlatives`
   * replays the whole frame, and a shorter game can land under a filter left
   * over from a longer one. A selected round that is no longer in the history
   * would filter every row away and render the filter above nothing, so the
   * selection is clamped at render time rather than trusted.
   */
  const round = picked !== null && !rounds.includes(picked) ? defaultRound(rounds) : picked;
  const setRound = setPicked;

  const visible = useMemo(
    () => (round === null ? history : history.filter((entry) => entry.round === round)),
    [history, round],
  );

  if (history.length === 0) {
    return (
      <div data-testid="history-empty" className="py-10 text-center">
        <p className="text-lg leading-tight font-semibold text-ink">
          No rankings to look back on.
        </p>
        <p className="mx-auto mt-2 max-w-[16rem] text-[0.8125rem] text-ink-soft">
          Every turn was skipped or voided.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="ranking-history">
      {rounds.length > 1 && (
        <div
          role="group"
          aria-label="Filter by round"
          data-testid="history-rounds"
          className="-mx-5 mb-3 flex gap-2 overflow-x-auto px-5 pb-1"
        >
          <RoundFilter
            label="All"
            active={round === null}
            onClick={() => {
              setRound(null);
              setOpenTurn(null);
            }}
          />
          {rounds.map((n) => (
            <RoundFilter
              key={n}
              testId={`history-round-${n}`}
              label={`Round ${n}`}
              active={round === n}
              onClick={() => {
                setRound(n);
                setOpenTurn(null);
              }}
            />
          ))}
        </div>
      )}

      <ol className="space-y-2">
        {visible.map((entry) => (
          <HistoryRow
            key={entry.turnIndex}
            entry={entry}
            players={players}
            selfId={selfId}
            open={openTurn === entry.turnIndex}
            onToggle={() =>
              setOpenTurn((current) => (current === entry.turnIndex ? null : entry.turnIndex))
            }
          />
        ))}
      </ol>
    </div>
  );
}

function RoundFilter({
  label,
  active,
  onClick,
  testId,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={`min-h-11 shrink-0 rounded-btn border px-3.5 text-[0.8125rem] font-semibold transition ${
        active ? 'border-terracotta bg-terracotta text-cream' : 'border-line bg-cream text-ink'
      }`}
    >
      {label}
    </button>
  );
}

function HistoryRow({
  entry,
  players,
  selfId,
  open,
  onToggle,
}: {
  entry: RankingHistoryEntry;
  players: ReadonlyMap<PlayerId, LicensePlayer>;
  selfId?: PlayerId;
  open: boolean;
  onToggle: () => void;
}) {
  const panelId = `history-panel-${entry.turnIndex}`;
  const last = entry.ranking.length - 1;

  return (
    <li
      data-testid={`history-entry-${entry.turnIndex}`}
      className="overflow-hidden rounded-card border border-line bg-cream"
    >
      {/*
        `role="button"` rather than a real <button>: the row's content includes
        a LicenseCard, which is a <div>, and flow content inside a <button> is
        invalid HTML. Keyboard activation is wired by hand to match.
      */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={open}
        aria-controls={panelId}
        data-testid={`history-toggle-${entry.turnIndex}`}
        className="w-full cursor-pointer px-3 py-3 text-left"
      >
        <div className="flex items-start gap-3">
          <span className="w-6 shrink-0 text-[0.9375rem] font-semibold text-green tabular-nums">
            {entry.questionNumber}
          </span>
          <span className="min-w-0 flex-1">
            <Eyebrow as="span" tone="ink-soft" className="block">
              Round {entry.round} · Turn {entry.turnIndex + 1}
            </Eyebrow>
            <span className="mt-1 block text-[0.875rem] leading-snug text-ink">
              {entry.questionText}
            </span>
          </span>
          <span
            aria-hidden="true"
            className={`shrink-0 text-xs text-ink-soft transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          >
            ▾
          </span>
        </div>
        {/* Who was ranking, as the same row every other player list uses. */}
        <div className="mt-2.5" data-testid={`history-author-${entry.turnIndex}`}>
          <LicenseCard
            player={playerOf(players, entry.turnPlayerId, selfId)}
            variant="chip"
            tone="neutral"
            className="bg-cream-deep!"
          />
        </div>
      </div>

      {open && (
        <div id={panelId} className="border-t border-line px-3 py-3">
          <Eyebrow className="pb-2">First to last</Eyebrow>
          <ol className="space-y-1.5" data-testid={`history-ranking-${entry.turnIndex}`}>
            {entry.ranking.map((playerId, index) => (
              <li key={playerId}>
                <LicenseCard
                  player={playerOf(players, playerId, selfId)}
                  variant="chip"
                  rank={index + 1}
                  tone={index === 0 ? 'top' : index === last ? 'bottom' : 'neutral'}
                />
              </li>
            ))}
          </ol>
        </div>
      )}
    </li>
  );
}
